/**
 * Auth context — provides current user state to the React app.
 * Supports magic link login, OAuth (Google/Microsoft), and post-login beta verification.
 */
import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { auth, setAccessToken, getAccessToken, takeCheckupToken, markCheckupTokenRedeemed, clearCheckupToken, clearPostAuthRedirect } from "./api.ts";
import { supabase } from "./supabase-client.ts";

// Use the current origin for OAuth redirects — works on both app.enereg.com and enereg.fly.dev
const SITE_URL = window.location.origin;

interface AuthUser {
  id: string;
  email: string;
  name: string;
  org_id: string;
  org_name: string;
  role?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  betaRequired: boolean;
  authError: string | null;
  sendMagicLink: (email: string) => Promise<{ error?: string }>;
  oAuthSignIn: (provider: "google" | "azure") => Promise<{ error?: string }>;
  verifyBeta: (code: string) => Promise<{ error?: string }>;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  betaRequired: false,
  authError: null,
  sendMagicLink: async () => ({}),
  oAuthSignIn: async () => ({}),
  verifyBeta: async () => ({}),
  logOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [betaRequired, setBetaRequired] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  // Persists across renders, unlike a closure `let` — the concurrency guard below depends on it.
  const exchangeInFlight = useRef<Promise<boolean> | null>(null);

  // Exchange the current Supabase Bearer token for a first-party session cookie, then load the user.
  // Without this, SSO (OAuth/magic-link) users hold only a Bearer token the server never validates,
  // so /auth/me returns null and they bounce back to the login screen (#15).
  //
  // Guarded against concurrent invocation: this fires from BOTH the mount check and the
  // onAuthStateChange SIGNED_IN listener, which on a fresh OAuth return both run with the same
  // token. Two exchanges would race to redeem one single-use checkup token — the loser hits the
  // checkup_invite PK and could flash "checkup link no longer valid" over a successful signup. The
  // in-flight promise makes the second caller await the first instead of racing it.
  function establishServerSession(): Promise<boolean> {
    if (exchangeInFlight.current) return exchangeInFlight.current;
    exchangeInFlight.current = doEstablishServerSession().finally(() => { exchangeInFlight.current = null; });
    return exchangeInFlight.current;
  }

  async function doEstablishServerSession(): Promise<boolean> {
    try {
      // A completed checkup IS the invite. Someone arriving from one carries a token (parked in
      // localStorage by LoginPage, so it survives both the OAuth full-page redirect AND a
      // magic-link new tab), so they go straight into their workspace instead of being asked for
      // a code they were never given — the whole point of the checkup being the first mile.
      // Pilot gate (Peter, 2026-07-15, AMDA launch): a completed checkup no longer
      // self-admits — access codes are issued on an onboarding call (see BetaActivation's
      // booking link), so first-timers land on the activation wall. Their email lives in
      // Supabase auth (the sign-in created that user); the app itself records nothing until
      // they're admitted. Flip to true to restore checkup-as-invite; the stash machinery
      // below is untouched, so pre-fill still works if admission happens within its TTL.
      const CHECKUP_TOKEN_ADMITS = false;
      const checkupToken = CHECKUP_TOKEN_ADMITS ? takeCheckupToken() : undefined;
      const result: any = await auth.exchangeSession(undefined, checkupToken);
      if (result?.needs_invite) {
        // First-time SSO user with no ticket — collect an invite code via the activation screen.
        setBetaRequired(true);
        setAuthError(null);
        return false;
      }
      // Spend the invite but KEEP the stash: on the magic-link path the wizard still needs the
      // token as its pre-fill capability (the fresh tab lost the URL params and the
      // sessionStorage redirect — this stash is the only carrier left). The redeemed mark keeps
      // the next sign-in replay-proof: takeCheckupToken never hands a redeemed token out again.
      if (checkupToken) markCheckupTokenRedeemed(checkupToken);
      setAuthError(null);
      return await fetchMe();
    } catch (e: any) {
      setAuthError(e?.message || "Sign-in failed. Please try again or contact your administrator.");
      setAccessToken(null);
      setUser(null);
      return false;
    }
  }

  async function fetchMe() {
    try {
      const result = await auth.getMe();
      if (result.user) {
        if (result.beta_required) {
          setBetaRequired(true);
          setUser(null); // Don't grant access yet
        } else {
          setBetaRequired(false);
          setUser(result.user);
        }
        return true;
      }
    } catch { /* no session */ }
    return false;
  }

  // Check session on mount
  useEffect(() => {
    async function checkSession() {
      // 1. Existing first-party cookie session (demo/password login, or a prior SSO exchange).
      if (await fetchMe()) { setLoading(false); return; }

      // 2. A stored Supabase token (SSO) — exchange it for a cookie, then load the user.
      const token = getAccessToken();
      if (token && await establishServerSession()) { setLoading(false); return; }

      // 3. A live Supabase session not yet reflected locally (e.g. fresh OAuth redirect).
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setAccessToken(session.access_token);
          if (await establishServerSession()) { setLoading(false); return; }
        }
      } catch { /* no session */ }

      setLoading(false);
    }
    checkSession();
  }, []);

  // Listen for auth state changes (OAuth callback, magic link)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        setAccessToken(session.access_token);
        await establishServerSession();
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSendMagicLink(email: string) {
    try {
      // Demo account: use local API login (demo user only exists in SQLite, not Supabase Auth)
      if (email.toLowerCase() === "demo@enereg.com") {
        // The shared demo account must never inherit a bystander's checkup: the stash was
        // written by whoever last clicked the CTA on this (possibly shared/booth) browser.
        // The CTA→demo flow itself still pre-fills via the URL params LoginRedirect preserves.
        clearCheckupToken();
        clearPostAuthRedirect();
        const result = await auth.logIn({ email, password: "testpass123" });
        if (result.user) {
          await fetchMe();
        }
        return { directLogin: true } as any;
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: SITE_URL + "/auth/callback" },
      });
      if (error) return { error: error.message };
      return {};
    } catch (e: any) {
      return { error: e.message || "Failed to send magic link" };
    }
  }

  async function handleOAuthSignIn(provider: "google" | "azure") {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: SITE_URL + "/auth/callback",
          scopes: provider === "azure" ? "email profile openid User.Read" : "email profile",
        },
      });
      if (error) return { error: error.message };
      return {};
    } catch (e: any) {
      return { error: e.message || "OAuth sign-in failed" };
    }
  }

  async function handleVerifyBeta(code: string) {
    try {
      // Exchange the SSO token again, now WITH the invite code → server provisions the workspace
      // (invite-code-after-OAuth) and sets the session cookie.
      const result: any = await auth.exchangeSession(code);
      if (result?.needs_invite) return { error: "A valid invite code is required" };
      setBetaRequired(false);
      setAuthError(null);
      await fetchMe();
      return {};
    } catch (e: any) {
      return { error: e.message || "Invalid invite code" };
    }
  }

  async function handleLogOut() {
    await auth.logOut().catch(() => {});
    await supabase.auth.signOut().catch(() => {});
    setAccessToken(null);
    // Shared-computer hygiene: the pre-fill stash and any parked redirect are this user's
    // checkup, not the next sign-in's. An explicit sign-out ends their life with the session.
    clearCheckupToken();
    clearPostAuthRedirect();
    setUser(null);
    setBetaRequired(false);
    setAuthError(null);
  }

  return (
    <AuthContext.Provider value={{
      user, loading, betaRequired, authError,
      sendMagicLink: handleSendMagicLink,
      oAuthSignIn: handleOAuthSignIn,
      verifyBeta: handleVerifyBeta,
      logOut: handleLogOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
